import React from 'react';
import { css } from '@emotion/css';
import { Notification } from 'is24-corecss';
import { GRAY300 } from '../../../../../../domain/colors';
import logo from '../images/axa-logo.svg';
import InsuranceStatus from './InsuranceStatus';
import { Msg, useIntl } from '@/i18n';
import { PlusBadge } from '@/components/common/display/plusBadge/PlusBadge';

const styles = {
  overviewBullets: css`
    list-style-type: initial;
    padding-left: 50px;
  `,
  overviewTab: { maxWidth: 530 },
  howItWorksTab: {
    subText: { maxWidth: 712, margin: '0 auto' },
    image: {
      width: '100%',
      height: 'auto',
      '@media (max-width: 668px)': { borderBottom: `1px solid ${GRAY300}` },
    },
  },
  statusFrame: css`
    border: 1px solid #d6d6d6;
    border-radius: 4px;
    padding: 24px 16px;
  `,
  statusFrameWrapper: css`
    display: flex;
    align-items: baseline;
    i {
      color: #333;
      font-size: 24px;
      transform: translateY(50%);
    }
    span {
      margin-left: 12px;
    }
  `,
  address: css`
    font-size: 14px;
    letter-spacing: 0;
    line-height: 18px;
    display: flex;
    align-items: flex-start;
  `,
  image: css`
    width: 72px;
    padding-right: 16px;
  `,
  text: css`
    font-size: 14px;
    line-height: 20px;
    padding-bottom: 24px;
  `,
  title: css`
    font-size: 18px;
    line-height: 22px;
    padding: 24px 0 16px 0;
  `,
};

export const AxaFooter = () => {
  const { t } = useIntl();
  return (
    <>
      <p className={styles.title}>
        {t({
          id: 'benefits.keyEmergency.modal.overview.request.keyEmergencyService.axaFooter.title',
          defaultMessage: 'Du hast deinen Schlüssel verloren und benötigst Hilfe?',
        })}
      </p>
      <p className={styles.text}>
        {t({
          id: 'benefits.keyEmergency.modal.overview.request.keyEmergencyService.axaFooter.text',
          defaultMessage: 'Mit dem Schlüsselnotdienst der AXA werden Kosten bis max. EUR 500,- übernommen.',
        })}
      </p>
      <div className={styles.address}>
        <img className={styles.image} src={logo} alt='AXA logo' />
        <div>
          <p>
            {t({
              id: 'benefits.keyEmergency.modal.overview.request.keyEmergencyService.axaFooter.companyName',
              defaultMessage: 'Inter Partner Assistance Service GmbH',
            })}
          </p>
          <p>
            {t({
              id: 'benefits.keyEmergency.modal.overview.request.keyEmergencyService.axaFooter.companyStreet',
              defaultMessage: 'Große Scharmstraße 36',
            })}
          </p>
          <p>
            {t({
              id: 'benefits.keyEmergency.modal.overview.request.keyEmergencyService.axaFooter.companyCity',
              defaultMessage: '15230 Frankfurt (Oder)',
            })}
          </p>
        </div>
      </div>
    </>
  );
};

const OverviewTab = ({
  isEntitled,
  isKeyEmergencyService,
}: {
  isEntitled: boolean;
  isKeyEmergencyService: boolean;
}) => {
  const { t } = useIntl();

  if (isKeyEmergencyService) {
    return (
      <div style={styles.overviewTab}>
        <Notification
          title={t({
            id: 'benefits.keyEmergency.modal.overview.request.keyEmergencyService.title',
            defaultMessage: 'Aktivierung erfolgreich',
          })}
          type='success'
          message={t({
            id: 'benefits.keyEmergency.modal.overview.request.keyEmergencyService.text',
            defaultMessage: 'Aktivierung erfolgreich',
          })}
        />
        <div className='margin-bottom-l' />
        <InsuranceStatus />
        <AxaFooter />
      </div>
    );
  }

  return (
    <div style={styles.overviewTab}>
      {isEntitled && (
        <div className='padding-bottom-l'>
          <Notification
            title={t({
              id: 'benefits.keyEmergency.modal.overview.request.title',
              defaultMessage: 'Aktivierung erfolgreich',
            })}
            type='info'
            message={t({
              id: 'benefits.keyEmergency.modal.overview.request.description',
              defaultMessage: 'Aktivierung erfolgreich',
            })}
          />
        </div>
      )}
      <div className='font-heading-xsmall-regular padding-bottom-l'>
        <Msg
          id='benefits.keyEmergency.modal.overview.paragraph1'
          defaultMessage='Abgesichert im Ernstfall mit dem Schlüsselnotdienst der AXA Assistance'
        />
      </div>
      <div className='padding-bottom-l'>
        <Msg
          id='benefits.keyEmergency.modal.overview.paragraph2'
          defaultMessage='Schlüssel verloren? Durch die AXA Assistance sind bei Notöffnung die Kosten bis zu 500 Euro abgedeckt.'
        />
      </div>
      <ul className='list-check list-spacing-s'>
        <li>
          <Msg
            id='benefits.keyEmergency.modal.overview.bullet1'
            defaultMessage='Beratung von geprüften Anwälten der AXA Assistance'
          />
        </li>
        <li>
          <Msg
            id='benefits.keyEmergency.modal.overview.bullet2'
            defaultMessage='Telefonische Erstberatung für bis zu drei Mietrechtsfragen im Jahr'
          />
        </li>
        <li>
          <Msg
            id='benefits.keyEmergency.modal.overview.bullet3'
            defaultMessage='Kosten bis max. EUR 500,- werden übernommen'
          />
        </li>
        <li>
          <Msg
            id='benefits.keyEmergency.modal.overview.bullet4'
            defaultMessage='In {BuyerPlusLogo} enthalten'
            values={{ BuyerPlusLogo: <PlusBadge variant='living' /> }}
          />
        </li>
      </ul>
    </div>
  );
};

export default OverviewTab;
